/**
 * Per-site access matrix, its read-only summary, and the review block frame
 * for the Add User wizard (`pages/InviteUser.tsx`, steps 3 and 4).
 */
import type { ReactNode } from 'react';
import type { UserSitePermissionFlags } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { FileCheck, CheckCircle, Eye, Mail } from 'lucide-react';
import { DOCUMENTS_NOTIFY_HINT, type PermissionFlags } from './constants';

interface PermissionMatrixProps {
  title: string;
  subtitle?: string;
  flags: PermissionFlags;
  onChange: (key: keyof UserSitePermissionFlags, value: boolean) => void;
  /** Documents only reaches customer-side users, so supplier invites hide it. */
  showDocuments?: boolean;
}

export function PermissionMatrix({ title, subtitle, flags, onChange, showDocuments = true }: PermissionMatrixProps) {
  return (
    <div className="border rounded-lg p-3 space-y-4">
      <div>
        <p className="font-medium text-sm">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>

      {/* CR-7076 (ADMIN-MODEL redesign): the per-site "User role" toggles are
          gone from the UI. Administrator and Approver wrote flags no backend
          check ever read (admin authority lives on the user-level role);
          the Compliance MODULE toggle stays hidden until the compliance
          module ships per-user gating. Its notification flag is shown as
          "Documents" (2026-10-14): it decides who hears about supplier
          document uploads and Read & Accept requests. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PermissionGroup label="Modules">
          <FlagRow icon={<FileCheck className="h-3.5 w-3.5" />} label="Purchasing" ariaLabel="Purchasing access"
            checked={flags.can_purchasing} onChange={v => onChange('can_purchasing', v)} />
          <FlagRow icon={<CheckCircle className="h-3.5 w-3.5" />} label="Quality" ariaLabel="Quality access"
            checked={flags.can_quality} onChange={v => onChange('can_quality', v)} />
        </PermissionGroup>

        <PermissionGroup label="Access Override">
          {/* Supersedes the user's role at this site: view everything,
              change nothing. */}
          <FlagRow icon={<Eye className="h-3.5 w-3.5" />} label="Read only"
            checked={flags.is_read_only} onChange={v => onChange('is_read_only', v)} />
        </PermissionGroup>

        <PermissionGroup label="Notifications">
          <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Procurement" ariaLabel="Procurement notifications"
            checked={flags.notify_procurement} onChange={v => onChange('notify_procurement', v)} />
          <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Quality" ariaLabel="Quality notifications"
            checked={flags.notify_quality} onChange={v => onChange('notify_quality', v)} />
          {showDocuments && (
            <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Documents" ariaLabel="Documents notifications"
              hint={DOCUMENTS_NOTIFY_HINT}
              checked={flags.notify_compliance} onChange={v => onChange('notify_compliance', v)} />
          )}
        </PermissionGroup>
      </div>
    </div>
  );
}

function PermissionGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

interface FlagRowProps {
  icon: ReactNode;
  label: string;
  /** Accessible name; defaults to `label` (two rows share "Quality"). */
  ariaLabel?: string;
  /** Optional one-line explanation shown under the switch. */
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function FlagRow({ icon, label, ariaLabel, hint, checked, onChange }: FlagRowProps) {
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5">{icon}{label}</span>
        <Switch checked={checked} onCheckedChange={onChange} aria-label={ariaLabel ?? label} />
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function PermissionSummary(
  { flags, showDocuments = true }: { flags: PermissionFlags; showDocuments?: boolean },
) {
  // CR-7076: no derived "Role:" line here. The old summary invented a
  // "Standard user" label from per-site toggles, contradicting the real
  // User Role shown one block up — the review now states the role exactly
  // once. The Compliance module stays hidden with its dormant toggle.
  const modules: string[] = [];
  if (flags.can_purchasing) modules.push('Purchasing');
  if (flags.can_quality) modules.push('Quality');
  const notify: string[] = [];
  if (flags.notify_procurement) notify.push('Procurement');
  if (flags.notify_quality) notify.push('Quality');
  if (showDocuments && flags.notify_compliance) notify.push('Documents');
  return (
    <div className="text-xs text-muted-foreground space-y-0.5">
      <div><strong>Modules:</strong> {modules.length ? modules.join(', ') : '—'}</div>
      {flags.is_read_only && (
        <div><strong>Access Override:</strong> Read only — can view, cannot change</div>
      )}
      <div><strong>Notifications:</strong> {notify.length ? notify.join(', ') : 'None'}</div>
    </div>
  );
}

export function ReviewBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border rounded-md p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">{label}</p>
      {children}
    </div>
  );
}
